import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { ReportColourMatrix } from "../components/ReportColourMatrix";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Loader2, Siren, Camera, FileText, CheckCircle2, X } from "lucide-react";
import dayjs from "dayjs";

const FAULTS = [
  { v: "third_party_at_fault", l: "Third party at fault" },
  { v: "renter_at_fault", l: "I was at fault" },
  { v: "shared", l: "Shared fault" },
  { v: "unknown", l: "Unknown / hit and run" },
];
const PLACES = ["Front", "Rear", "Left side", "Right side", "Roof", "Windshield", "Interior", "Multiple areas"];

export default function ReportAccident() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [bookings, setBookings] = useState(null);
  const [form, setForm] = useState({
    booking_ref: params.get("booking") || "",
    accident_datetime: dayjs().format("YYYY-MM-DDTHH:mm"),
    location: "", description: "", fault: "third_party_at_fault", damage_place: "Front",
    damage_description: "", remarks: "",
  });
  const [tp, setTp] = useState({ involved: false, company: "", driver_name: "", vehicle_details: "", insurance_policy: "", injuries: false });
  const [police, setPolice] = useState({ involved: false, report_no: "" });
  const [photos, setPhotos] = useState([]);
  const [policeFile, setPoliceFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (user === null) navigate("/login?next=/report-accident");
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/bookings").then((r) => {
      const list = r.data.filter((b) => b.status !== "cancelled");
      setBookings(list);
      setForm((f) => ({ ...f, booking_ref: f.booking_ref || list[0]?.booking_ref || "" }));
    }).catch(() => setBookings([]));
  }, [user]);

  const selBooking = useMemo(() => bookings?.find((b) => b.booking_ref === form.booking_ref), [bookings, form.booking_ref]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.booking_ref) { toast.error("Select a booking"); return; }
    setSubmitting(true);
    try {
      const { data: report } = await api.post("/accidents", {
        ...form,
        third_party: tp.involved ? tp : { involved: false },
        police: police.involved ? police : { involved: false },
      });
      const attachments = [
        ...photos.map((file) => ({ kind: "damage_photo", file })),
        ...(policeFile ? [{ kind: "police_report", file: policeFile }] : []),
      ];
      let uploaded = 0;
      let failed = 0;
      for (const { kind, file } of attachments) {
        const fd = new FormData();
        fd.append("kind", kind);
        fd.append("file", file);
        try {
          await api.post(`/accidents/${report.report_id}/attachments`, fd);
          uploaded++;
        } catch (uploadErr) {
          failed++;
          console.error("Attachment upload failed", uploadErr);
        }
      }
      if (failed > 0) toast.warning(`${failed} attachment${failed > 1 ? "s" : ""} could not be uploaded — our team will follow up.`);
      setDone({ ...report, uploaded });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const shell = (content) => (
    <div className="min-h-screen flex flex-col bg-[#fafcfa]">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 w-full flex-1">{content}</main>
      <Footer />
    </div>
  );

  if (!user || bookings === null) return shell(<div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#009900]" /></div>);

  if (done)
    return shell(
      <div className="text-center fade-up" data-testid="accident-success">
        <CheckCircle2 className="w-16 h-16 text-[#009900] mx-auto mb-4" />
        <h1 className="heading-upper text-2xl sm:text-3xl text-[#144733]">Report submitted</h1>
        <p className="text-sm text-[#57685e] mt-2">{done.uploaded} file{done.uploaded !== 1 ? "s" : ""} attached. Our team will review and contact you — a replacement vehicle is arranged after final-report validation.</p>
        <div className="inline-block bg-[#144733] text-white rounded-lg px-8 py-4 mt-6">
          <div className="text-[10px] uppercase tracking-widest text-[#FFDC00]">Accident report reference</div>
          <div className="text-2xl font-bold tracking-wider" data-testid="accident-reference">{done.report_id}</div>
        </div>
        <div className="mt-8">
          <Link to="/account" className="btn-cta rounded px-6 py-3 text-sm inline-block" data-testid="accident-back-account-btn">Back to My Account</Link>
        </div>
      </div>
    );

  if (bookings.length === 0)
    return shell(
      <div className="space-y-6">
        <div className="bg-white border border-[#e2ebe4] rounded-lg p-10 text-center text-sm text-[#57685e]">
          You need an active booking to report an accident. <Link to="/" className="underline text-[#009900]">Book a car</Link>
        </div>
        <ReportColourMatrix />
      </div>
    );

  return shell(
    <form onSubmit={submit} data-testid="accident-form">
      <div className="promo-stripe mb-5">
        <h1 className="heading-upper text-2xl text-[#144733]">Report an accident</h1>
        <p className="text-sm text-[#57685e] mt-1">Submitted to the Accident Report API (mocked in demo).</p>
      </div>

      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3" data-testid="accident-emergency-banner">
        <Siren className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
        <div className="text-sm text-red-800">
          <strong>In an emergency call 999 first.</strong> Roadside assistance: <strong>800 EUROPCAR</strong>. Check safety, obtain the final authorized police report (not just a reference number), and never admit fault at the scene.
        </div>
      </div>

      <div className="mb-6">
        <ReportColourMatrix />
      </div>

      <div className="space-y-6">
        <div className="bg-white rounded-lg border border-[#e2ebe4] p-5 space-y-4">
          <h2 className="heading-upper text-sm tracking-widest text-[#144733]">Accident details</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="field-label">Booking *</label>
              <select className="field-input" value={form.booking_ref} onChange={(e) => setForm({ ...form, booking_ref: e.target.value })} data-testid="accident-booking-select">
                {bookings.map((b) => <option key={b.booking_ref} value={b.booking_ref}>{b.booking_ref} · {b.vehicle.name} · {dayjs(b.pickup_datetime).format("DD MMM")}</option>)}
              </select>
              {selBooking && <p className="text-[11px] text-[#8a978f] mt-1">{selBooking.vehicle.category} · {selBooking.display_status}</p>}
            </div>
            <div>
              <label className="field-label">Date & time of accident *</label>
              <input type="datetime-local" required className="field-input" max={dayjs().format("YYYY-MM-DDTHH:mm")} value={form.accident_datetime}
                onChange={(e) => setForm({ ...form, accident_datetime: e.target.value })} data-testid="accident-datetime-input" />
            </div>
            <div>
              <label className="field-label">Location *</label>
              <input required className="field-input" placeholder="e.g. Sheikh Zayed Road, exit 42" value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="accident-location-input" />
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">What happened? *</label>
              <textarea required rows={3} className="field-input" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="accident-description-input" />
            </div>
            <div>
              <label className="field-label">Who was at fault?</label>
              <select className="field-input" value={form.fault} onChange={(e) => setForm({ ...form, fault: e.target.value })} data-testid="accident-fault-select">
                {FAULTS.map((f) => <option key={f.v} value={f.v}>{f.l}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Damage location</label>
              <select className="field-input" value={form.damage_place} onChange={(e) => setForm({ ...form, damage_place: e.target.value })} data-testid="accident-damage-place-select">
                {PLACES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">Damage description</label>
              <input className="field-input" placeholder="e.g. dented front bumper, broken left headlight" value={form.damage_description}
                onChange={(e) => setForm({ ...form, damage_description: e.target.value })} data-testid="accident-damage-desc-input" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-[#e2ebe4] p-5">
          <h2 className="heading-upper text-sm tracking-widest text-[#144733] mb-3 flex items-center gap-2"><Camera className="w-4 h-4" /> Damage photos</h2>
          <input type="file" multiple accept="image/jpeg,image/png,image/webp,image/bmp" className="hidden" id="photos-input"
            onChange={(e) => { setPhotos((p) => [...p, ...Array.from(e.target.files || [])].slice(0, 6)); e.target.value = ""; }}
            data-testid="accident-photos-input" />
          <label htmlFor="photos-input" className="inline-block border-2 border-[#009900] text-[#009900] rounded px-4 py-2.5 text-xs font-medium uppercase tracking-wide cursor-pointer hover:bg-[#f0f9f0]">
            Add photos (max 6)
          </label>
          {photos.length > 0 && (
            <div className="flex flex-wrap gap-3 mt-4">
              {photos.map((f, i) => (
                <div key={`${f.name}-${f.lastModified}`} className="relative">
                  <img src={URL.createObjectURL(f)} alt={f.name} className="w-24 h-20 object-cover rounded border border-[#e2ebe4]" />
                  <button type="button" onClick={() => setPhotos((p) => p.filter((_, j) => j !== i))}
                    className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center"
                    data-testid={`remove-photo-${i}`}><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border border-[#e2ebe4] p-5 space-y-4">
          <label className="flex items-center gap-3 cursor-pointer" data-testid="police-toggle">
            <input type="checkbox" checked={police.involved} onChange={(e) => setPolice({ ...police, involved: e.target.checked })} className="accent-[#009900]" />
            <span className="heading-upper text-sm tracking-widest text-[#144733] flex items-center gap-2"><FileText className="w-4 h-4" /> Police involved</span>
          </label>
          {police.involved && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="field-label">Police report number</label>
                <input className="field-input" value={police.report_no} onChange={(e) => setPolice({ ...police, report_no: e.target.value })} data-testid="police-report-no-input" />
              </div>
              <div>
                <label className="field-label">Police report file (image/PDF)</label>
                <input type="file" accept="image/jpeg,image/png,application/pdf" className="field-input"
                  onChange={(e) => setPoliceFile(e.target.files?.[0] || null)} data-testid="police-report-file-input" />
              </div>
              <p className="sm:col-span-2 text-[11px] text-[#8a978f]">Insurance claims require the <strong>final authorized report</strong> — a reference number alone is not sufficient.</p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border border-[#e2ebe4] p-5 space-y-4">
          <label className="flex items-center gap-3 cursor-pointer" data-testid="third-party-toggle">
            <input type="checkbox" checked={tp.involved} onChange={(e) => setTp({ ...tp, involved: e.target.checked })} className="accent-[#009900]" />
            <span className="heading-upper text-sm tracking-widest text-[#144733]">Third party involved</span>
          </label>
          {tp.involved && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="field-label">Driver name</label>
                <input className="field-input" value={tp.driver_name} onChange={(e) => setTp({ ...tp, driver_name: e.target.value })} data-testid="tp-driver-input" />
              </div>
              <div>
                <label className="field-label">Company (if commercial)</label>
                <input className="field-input" value={tp.company} onChange={(e) => setTp({ ...tp, company: e.target.value })} data-testid="tp-company-input" />
              </div>
              <div>
                <label className="field-label">Vehicle details (make, plate)</label>
                <input className="field-input" value={tp.vehicle_details} onChange={(e) => setTp({ ...tp, vehicle_details: e.target.value })} data-testid="tp-vehicle-input" />
              </div>
              <div>
                <label className="field-label">Insurance policy no.</label>
                <input className="field-input" value={tp.insurance_policy} onChange={(e) => setTp({ ...tp, insurance_policy: e.target.value })} data-testid="tp-policy-input" />
              </div>
              <label className="sm:col-span-2 flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={tp.injuries} onChange={(e) => setTp({ ...tp, injuries: e.target.checked })} className="accent-[#009900]" data-testid="tp-injuries-checkbox" />
                Injuries were reported
              </label>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border border-[#e2ebe4] p-5">
          <h2 className="heading-upper text-sm tracking-widest text-[#144733] mb-3">Remarks / other details</h2>
          <textarea rows={3} className="field-input" placeholder="Anything else we should know…" value={form.remarks}
            onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="accident-remarks-input" />
        </div>

        <button type="submit" disabled={submitting} className="btn-cta rounded w-full py-4 text-sm flex items-center justify-center gap-2" data-testid="accident-submit-btn">
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Submit accident report
        </button>
      </div>
    </form>
  );
}
